import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/client';
import { Loader2, Mail, Phone, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

const STATUSES = ['new', 'contacted', 'booked', 'archived'];
const STATUS_COLORS = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-amber-100 text-amber-700', booked: 'bg-emerald-100 text-emerald-700', archived: 'bg-slate-100 text-slate-500' };

const AdminLeads = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      setItems(await adminApi.listLeads(params));
    } catch {} setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (lead, status) => {
    try { await adminApi.updateLead(lead.id, { status }); toast({ title: 'Status updated' }); load(); }
    catch { toast({ title: 'Update failed', variant: 'destructive' }); }
  };

  const del = async (lead) => {
    if (!window.confirm(`Delete inquiry from ${lead.name}?`)) return;
    try { await adminApi.deleteLead(lead.id); load(); } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-3xl text-[#304b71]">Inquiries / Leads</h1><p className="text-slate-500 text-sm mt-1">All booking inquiries submitted from the website.</p></div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Filter:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#304b71]" /></div> :
          items.length === 0 ? <div className="py-20 text-center text-slate-400">No inquiries yet.</div> :
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-[#f6f8fa]"><tr><th className="text-left px-5 py-3">Name</th><th className="text-left">Contact</th><th className="text-left">Subject</th><th className="text-left">Travel</th><th className="text-left">Status</th><th className="text-left">Received</th><th className="text-right px-5">Actions</th></tr></thead>
            <tbody>{items.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 hover:bg-[#f6f8fa]/50 cursor-pointer" onClick={() => setDetail(l)}>
                <td className="px-5 py-3 font-serif text-[#304b71]">{l.name}</td>
                <td className="text-slate-600"><div className="flex items-center gap-2"><Mail size={12} /> {l.email}</div>{l.phone && <div className="flex items-center gap-2 mt-1 text-xs"><Phone size={11} /> {l.phone}</div>}</td>
                <td className="text-slate-600 max-w-xs">{l.subject}{l.reference_name && <div className="text-xs text-slate-400">Ref: {l.reference_name}</div>}</td>
                <td className="text-slate-600 text-xs">{l.travel_date || '—'}<div>{l.travelers ? `${l.travelers} pax` : ''}</div></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                    <SelectTrigger className={`w-32 h-7 text-xs capitalize ${STATUS_COLORS[l.status] || ''}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="text-slate-500 text-xs">{new Date(l.created_at).toLocaleDateString()}<div>{new Date(l.created_at).toLocaleTimeString()}</div></td>
                <td className="text-right px-5" onClick={(e) => e.stopPropagation()}><Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => del(l)}><Trash2 size={14} /></Button></td>
              </tr>
            ))}</tbody>
          </table>
        }
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-[#304b71]">Inquiry Details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Name</span><span className="col-span-2">{detail.name}</span></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Email</span><a href={`mailto:${detail.email}`} className="col-span-2 text-[#304b71]">{detail.email}</a></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Phone</span><a href={`tel:${detail.phone}`} className="col-span-2 text-[#304b71]">{detail.phone || '—'}</a></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Subject</span><span className="col-span-2">{detail.subject}</span></div>
              {detail.reference_name && <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Regarding</span><span className="col-span-2">{detail.reference_name}</span></div>}
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Travel Date</span><span className="col-span-2">{detail.travel_date || '—'}</span></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Travelers</span><span className="col-span-2">{detail.travelers || '—'}</span></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Message</span><span className="col-span-2 whitespace-pre-line">{detail.message || '—'}</span></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-slate-500">Received</span><span className="col-span-2">{new Date(detail.created_at).toLocaleString()}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeads;
